import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import DatePicker from 'react-datepicker';
import moment from 'moment';

import Formsy from '../../external/Formsy';
import ParticipationSchema from '../../data/schema/ParticipationSchema';
import participationAction from '../../actions/ParticipationActions';
import {RELATIONSHIP, GUARDIAN_INFO_KEYMAPS} from '../../constants/PayoutConstants';
import MismatchModal from '../../components/payout/participation/MismatchModal';
import { withContentGated } from '../../components/access/LegalAccess';
import { GAME_TITLE } from '../../constants/GameTitle';

import {
    AccountId,
    getMessage,
    ValidatingInput,
    Button,
    LoginWrapper,
    LoadingInspector,
    ValidatingSelect,
    AccessError,
    NotReady
} from '../../components';

import ErrorModal from '../../components/common/modal/ErrorModal';
import Svg from '../../components/common/svg/Svg';

import { getGameImage, getGameTitle } from '../../utils/Utils';

const mapStateToProps = state => {
    return {
        configState: state.get('config').toJS(),
        messages: state.get('localization').get('messageProperties'),
        participation: state.get('participation').toJS(),
        userState: state.get('user').toJS(),
        legalState: state.get('legal').toJS()
    };
};

const mapDispatchToProps = {
    setPlayerForm: participationAction.setPlayerForm,
    saveForm: participationAction.saveForm,
    hideErrorModal: participationAction.hideErrorModal
};

@connect(mapStateToProps, mapDispatchToProps)
@withContentGated(GAME_TITLE.ROCKET_LEAGUE)
class ParticipationForm extends React.Component {
    static propTypes = {
        configState: PropTypes.object,
        hideErrorModal: PropTypes.func,
        match: PropTypes.object,
        messages: PropTypes.object,
        participation: PropTypes.object,
        saveForm: PropTypes.func,
        setPlayerForm: PropTypes.func,
        userState: PropTypes.object,
        history: PropTypes.object,
        isMinor: PropTypes.bool
    };

    getMsg(code, args) {
        return getMessage(this.props.messages, code, args);
    }

    get hasToken() {
        return this.props.match && this.props.match.params && this.props.match.params.id;
    }

    get participationSchema() {
        const { messages } = this.props;
        return new ParticipationSchema(messages);
    }

    get relationshipOptions() {
        return [
            {value: RELATIONSHIP.PARENT, label: this.participationSchema.relationship.optionLabel.parent},
            {value: RELATIONSHIP.LEGAL_GUARDIAN, label: this.participationSchema.relationship.optionLabel.guardian}
        ];
    }

    handlePlayerChange = (event) => {
        const { setPlayerForm, participation: { playerInfo } } = this.props;
        const newPlayer = {
            ...playerInfo,
            [event.target.name]: (event.target.value && event.target.value.trimLeft()) || ''
        };
        setPlayerForm(newPlayer);
    };

    ParticipationHeader = () => {
        const { userState, participation: {eventInfo} } = this.props;
        return (
            <div>
                <div className="participation-header">
                    <Svg image={getGameImage(eventInfo.gameId)} className="logo" />
                    <div className="desc">
                        <h3>{this.getMsg('epic.esport.participation.header')}</h3>
                    </div>
                    <div className="account-id">
                        <AccountId
                            id={userState.accountInfo.id}
                            showLabel={this.getMsg('epic.esport.prizes.header.id.show')}
                            hideLabel={this.getMsg('epic.esport.prizes.header.id.hide')}
                        />
                    </div>
                </div>
                <div className="participation-header-desc">
                    <h3>{this.getMsg('epic.esport.participation.legal.intro.title')}</h3>
                    <p>{this.getMsg('epic.esport.participation.legal.intro.text', [eventInfo.eventName || ''])}</p>
                </div>
            </div>
        );
    };

    CustomInput = ({value, onClick, name, inputPlaceholder, disabled}) => {
        const { participation } = this.props;
        return (
            <div onClick={onClick}>
                <ValidatingInput
                    customClassNames="lg"
                    value={value}
                    name={name}
                    placeholder={inputPlaceholder}
                    validationErrors={this.participationSchema.firstName.validationMessages}
                    disabled={participation.agreed || disabled}
                    required
                />
            </div>
        );
    };

    ParticipationForm = () => {
        const { participation, userState: {accountInfo} = {}, isMinor: accountisMinor } = this.props;
        const { playerInfo, eventInfo } = participation;
        const dateString = (eventInfo.eventStartDate ? moment(eventInfo.eventStartDate) : moment()).format('MMM D, YYYY');
        let dateBirth = accountInfo.dateOfBirth || playerInfo.dateOfBirth;
        dateBirth = (dateBirth ? moment(dateBirth) : '');
        return (
            <div className="section-wrapper">
                <h4>{this.getMsg('epic.esport.participation.form.event.title')}</h4>
                <div className="row">
                    <ValidatingInput
                        customClassNames="col-xs-12 col-lg-6"
                        value={eventInfo.eventName}
                        name={this.participationSchema.eventName.identifier}
                        placeholder={this.participationSchema.eventName.display.text}
                        disabled
                    />
                    <ValidatingInput
                        customClassNames="col-xs-12 col-lg-6"
                        value={eventInfo.eventOrganizer}
                        name={this.participationSchema.eventOrg.identifier}
                        placeholder={this.participationSchema.eventOrg.display.text}
                        disabled
                    />
                </div>

                <div className="row">
                    <ValidatingInput
                        customClassNames="col-xs-12 col-lg-6"
                        value={dateString}
                        name={this.participationSchema.eventDate.identifier}
                        placeholder={this.participationSchema.eventDate.display.text}
                        disabled
                    />

                    <ValidatingInput
                        onChange={this.handlePlayerChange}
                        customClassNames="col-xs-12 col-lg-6"
                        value={playerInfo.email}
                        name={this.participationSchema.emailAddress.identifier}
                        validations={this.participationSchema.emailAddress.validations}
                        validationErrors={this.participationSchema.emailAddress.validationMessages}
                        placeholder={this.participationSchema.emailAddress.display.text}
                        disabled
                        required
                    />
                </div>

                <h4>{this.getMsg('epic.esport.participation.form.player.title')}</h4>
                <div className="row">
                    <ValidatingInput
                        onChange={this.handlePlayerChange}
                        customClassNames="col-xs-12 col-lg-6"
                        value={playerInfo.firstName}
                        validationErrors={this.participationSchema.firstName.validationMessages}
                        name={this.participationSchema.firstName.identifier}
                        placeholder={this.participationSchema.firstName.display.text}
                        disabled={participation.agreed}
                        required
                    />
                    <span />
                    <ValidatingInput
                        onChange={this.handlePlayerChange}
                        customClassNames="col-xs-12 col-lg-6"
                        value={playerInfo.lastName}
                        name={this.participationSchema.lastName.identifier}
                        validationErrors={this.participationSchema.lastName.validationMessages}
                        placeholder={this.participationSchema.lastName.display.text}
                        disabled={participation.agreed}
                        required
                    />
                    <span />
                    <div className="form-field-wrapper" />
                </div>
                <div className="row">

                    <div className="col-xs-12 col-lg-6">
                        <DatePicker
                            onChange={(date) => {
                                const localDate = date.format('YYYY-MM-DD');
                                const currentPlayer = { ...playerInfo, dateOfBirth: localDate };
                                const elapsedYears = moment.utc().diff(date, 'years');
                                if (elapsedYears < 18) {
                                    currentPlayer.relationship = 'Parent';
                                    currentPlayer.isMinor = true;
                                } else {
                                    currentPlayer.isMinor = false;
                                }
                                this.props.setPlayerForm(currentPlayer);
                            }}
                            showMonthDropdown
                            dropdownMode="select"
                            customClassNames="lg"
                            showYearDropdown
                            maxDate={moment().subtract(15, 'years')}
                            disabled={accountisMinor || participation.agreed}
                            name={this.participationSchema.birthDay.identifier}
                            selected={dateBirth}
                            customInput={<this.CustomInput
                                name={this.participationSchema.birthDay.identifier}
                                disabled={accountisMinor || participation.agreed}
                                inputPlaceholder={this.participationSchema.birthDay.display.text}
                            />}
                        />
                    </div>
                    <ValidatingInput
                        customClassNames="col-xs-12 col-lg-6"
                        value={`${moment.utc().diff(dateBirth, 'years') || playerInfo.elapsedYears}`}
                        name={this.participationSchema.age.identifier}
                        placeholder={this.participationSchema.age.display.text}
                        disabled
                    />
                </div>
            </div>
        );
    };

    ParticipationText = () => {
        const { participation } = this.props;
        const { eventInfo } = participation;
        return (
            <div className="section-wrapper section-text">
                <h4>{this.getMsg('epic.esport.participation.legal.player_conduct.title')}</h4>
                <p>{this.getMsg('epic.esport.participation.legal.player_conduct.text.without.game', [getGameTitle(eventInfo.gameId)])}</p>

                <h4>{this.getMsg('epic.esport.participation.legal.name_likeness.title')}</h4>
                <p>{this.getMsg('epic.esport.participation.legal.name_likeness.text.without.game', [getGameTitle(eventInfo.gameId)])}</p>

                <h4>{this.getMsg('epic.esport.participation.legal.limitations.title')}</h4>
                <p>{this.getMsg('epic.esport.participation.legal.limitations.text1.without.game', [getGameTitle(eventInfo.gameId)])}</p>
                <p>{this.getMsg('epic.esport.participation.legal.limitations.text2')}</p>

                <h4>{this.getMsg('epic.esport.participation.legal.other_terms.title')}</h4>
                <p>{this.getMsg('epic.esport.participation.legal.other_terms.text.without.game', [getGameTitle(eventInfo.gameId)])}</p>
            </div>
        );
    };

    submit = (formData) => {
        const { participation: { playerInfo, eventInfo }, isMinor: accountisMinor, userState: {accountInfo} = {} } = this.props;
        const payloadPlayer = {...playerInfo};
        if (!payloadPlayer.dateOfBirth && formData.dateOfBirth) {
            payloadPlayer.dateOfBirth = moment(formData.dateOfBirth).format('YYYY-MM-DD');
            payloadPlayer.elapsedYears = moment().diff(formData.dateOfBirth, 'years');
        }
        if (accountisMinor) {
            payloadPlayer.isMinor = true;
            payloadPlayer.dateOfBirth = accountInfo.dateOfBirth;
            payloadPlayer.elapsedYears = moment().diff(payloadPlayer.dateOfBirth, 'years');
        }
        if (!payloadPlayer.isMinor) {
            GUARDIAN_INFO_KEYMAPS.forEach((kName) => {
                delete payloadPlayer[kName];
            });
        }
        this.props.saveForm({playerInfo: payloadPlayer, eventInfo, history: this.props.history});
    };

    ParticipationPlayerInfo = () => {
        const { participation, isMinor: accountisMinor } = this.props;
        const { playerInfo } = participation;
        const isMinor = accountisMinor || playerInfo.isMinor;
        return (
            <div className="section-text section-submission">
                <h4 className="signature-title">
                    {this.getMsg('epic.esport.participation.legal.form.player_signature')}
                </h4>
                <div className="row">
                    <ValidatingInput
                        value={playerInfo.signature}
                        customClassNames="col-xs-12 col-lg-6"
                        onChange={this.handlePlayerChange}
                        name={this.participationSchema.signature.identifier}
                        validations={this.participationSchema.signature.validations}
                        validationErrors={this.participationSchema.signature.validationMessages}
                        placeholder={this.participationSchema.signature.display.text}
                        disabled={participation.agreed}
                        required
                    />
                    <span />
                    {
                        !!playerInfo.signatureDate && <ValidatingInput
                            value={moment(playerInfo.signatureDate).format('MMM D, YYYY')}
                            customClassNames="col-xs-12 col-lg-6"
                            onChange={this.handlePlayerChange}
                            name={this.participationSchema.signature.identifier}
                            validationErrors={this.participationSchema.signature.validationMessages}
                            placeholder={this.participationSchema.signatureDate.display.text}
                            disabled
                            required
                        />
                    }
                    <span />
                    <div className="form-field-wrapper" />
                </div>
                { isMinor && <this.ParticipationGuardianInfo /> }
                <Button disabled={participation.agreed}>
                    {this.getMsg('epic.common.submit_form')}
                </Button>
            </div>
        );
    };

    ParticipationGuardianInfo = () => {
        const { participation: { playerInfo, agreed } } = this.props;
        return [
            <h4 className="guardian-title" key="guardian-title">
                {this.getMsg('epic.esport.participation.legal.form.title.guardian')}
            </h4>,
            <span>{this.getMsg('epic.esport.participation.legal.form.minor_definition')}</span>,
            <div key="guardian-info" className="row">
                <ValidatingInput
                    customClassNames="col-xs-12 col-lg-6"
                    value={playerInfo.guardianFirstName}
                    onChange={this.handlePlayerChange}
                    validationErrors={this.participationSchema.guardianFirstName.validationMessages}
                    name={this.participationSchema.guardianFirstName.identifier}
                    disabled={agreed}
                    placeholder={this.participationSchema.guardianFirstName.display.text}
                    required
                />
                <ValidatingInput
                    customClassNames="col-xs-12 col-lg-6"
                    onChange={this.handlePlayerChange}
                    value={playerInfo.guardianLastName}
                    validationErrors={this.participationSchema.guardianLastName.validationMessages}
                    name={this.participationSchema.guardianLastName.identifier}
                    disabled={agreed}
                    placeholder={this.participationSchema.guardianLastName.display.text}
                    required
                />
            </div>,
            <div key="guardian-sign" className="row">
                <ValidatingInput
                    customClassNames="col-xs-12 col-lg-6"
                    value={playerInfo.guardianSignature}
                    onChange={this.handlePlayerChange}
                    validations={this.participationSchema.guardianSignature.validations}
                    validationErrors={this.participationSchema.guardianSignature.validationMessages}
                    name={this.participationSchema.guardianSignature.identifier}
                    disabled={agreed}
                    placeholder={this.participationSchema.guardianSignature.display.text}
                    required
                />
                <span />
                <ValidatingSelect
                    customClassNames="col-xs-12 col-lg-6"
                    name={this.participationSchema.relationship.identifier}
                    validationErrors={this.participationSchema.relationship.validationMessages}
                    value={playerInfo.relationship}
                    options={this.relationshipOptions}
                    onChange={this.handlePlayerChange}
                    disabled={agreed}
                    required
                />
            </div>
        ];
    };

    hideModel = () => {
        this.props.hideErrorModal();
    };

    FormInfo = () => {
        const {
            configState,
            participation,
            userState
        } = this.props;
        const {error, invalid} = participation;
        const {supportUrl} = configState;
        const supportSiteName = this.getMsg('epic.common.support_site');

        if (!userState.isLoggedIn) {
            return <LoginWrapper />;
        }
        if (!this.hasToken) {
            return (
                <div id="content" className="container">
                    <NotReady
                        supportName={supportSiteName}
                        supportUrl={supportUrl}
                    />
                </div>
            );
        }

        if (invalid) {
            return (
                <div id="content" className="container">
                    <AccessError
                        supportName={supportSiteName}
                        supportUrl={supportUrl}
                    />
                </div>
            );
        }
        return (
            <div className="container participation-form">
                <LoadingInspector loading={participation.loading}>
                    <this.ParticipationHeader />
                    <Formsy onValidSubmit={this.submit} >
                        <this.ParticipationForm />
                        <this.ParticipationText />
                        <this.ParticipationPlayerInfo />
                    </Formsy>
                </LoadingInspector>
                <ErrorModal
                    showDialog={!!error}
                    title="epic.esport.error.modal.title"
                    message={error && error.errorCode}
                    closeModal={this.hideModel} />
            </div>
        );
    };

    render() {
        const { match: { params = {} }, participation: { mismatch }, userState } = this.props;
        const formToken = params.id || '';
        return (
            [
                <this.FormInfo key="form-info" />,
                <MismatchModal
                    key="mismatchModal"
                    formToken={formToken}
                    showDialog={mismatch}
                    title="epic.esport.error.modal.title"
                    closeModal={this.hideModel}
                    userState={userState}
                />
            ]
        );
    }
}

export default ParticipationForm;
